import { useEffect, useState } from 'react';
import { Loader2, RotateCcw, Trash2, X } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';

/**
 * Einstellungen-Dialog fuer eine persoenliche Uebersicht-Seite.
 * Umbenennen + Loeschen in einem Modal. Loesch-Bestaetigung als zweistufiger
 * Toggle innerhalb des Dialogs (kein extra Modal), da die Aktion klein ist.
 *
 * Konzept: 04 Ressourcen/Softwarearchitektur/Widget-Picker-Konzept.md
 */

interface Props {
  open: boolean;
  pageId: string;
  initialName: string;
  /** true wenn die Seite aus einer Vorlage stammt — Reset-Button wird nur dann gezeigt. */
  hasTemplate: boolean;
  /** Admin-Mode (Joerg 2026-06-08): wenn gesetzt, schreiben Rename/Delete/Reset
   *  gegen /admin/users/:actAsUserId/pages/:id statt /my-pages/:id. */
  actAsUserId?: string;
  onClose: () => void;
  /** Wird aufgerufen wenn Name geaendert wurde — Parent reloaded Tabs + Header. */
  onRenamed: (newName: string) => void;
  /** Wird aufgerufen nach erfolgreichem Loeschen — Parent navigiert weg. */
  onDeleted: () => void;
  /** Wird aufgerufen nach erfolgreichem Reset — Parent reloaded die Seite mit dem Vorlage-Layout. */
  onReset: () => void;
}

export default function PageSettingsDialog({ open, pageId, initialName, hasTemplate, actAsUserId, onClose, onRenamed, onDeleted, onReset }: Props) {
  const baseUrl = actAsUserId
    ? `/portal/admin/users/${actAsUserId}/pages/${pageId}`
    : `/portal/my-pages/${pageId}`;
  const [name, setName] = useState(initialName);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [busy, setBusy] = useState<'rename' | 'delete' | 'reset' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setConfirmDelete(false);
      setConfirmReset(false);
      setBusy(null);
      setError(null);
    }
  }, [open, initialName]);

  if (!open) return null;

  const trimmed = name.trim();
  const nameChanged = trimmed && trimmed !== initialName;

  const doRename = async () => {
    if (!nameChanged) return;
    setBusy('rename');
    setError(null);
    try {
      const r = await api.patch<{ success: boolean }>(baseUrl, { name: trimmed });
      if (r.success) {
        onRenamed(trimmed);
        onClose();
      } else {
        setError('Speichern fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async () => {
    setBusy('delete');
    setError(null);
    try {
      const r = await api.delete<{ success: boolean }>(baseUrl);
      if (r.success) {
        onDeleted();
      } else {
        setError('Löschen fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const doReset = async () => {
    setBusy('reset');
    setError(null);
    try {
      const r = await api.post<{ success: boolean; error?: string }>(`${baseUrl}/reset`, {});
      if (r.success) {
        onReset();
        onClose();
      } else {
        setError(r.error || 'Zurücksetzen fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-card border border-default rounded-xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-5 py-3 border-b border-default">
          <h2 className="text-lg font-semibold">Seite verwalten</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schliessen">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <label className="block">
            <div className="text-sm font-medium mb-1">Name</div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              className="w-full px-3 py-2 rounded-lg border border-default bg-card text-foreground"
            />
          </label>

          {error && <div className="text-sm text-red-500">{error}</div>}

          <div className="flex justify-end items-center gap-2">
            <button
              onClick={doRename}
              disabled={!nameChanged || busy !== null}
              className="px-3 py-2 rounded-lg bg-brand-600 text-primary-foreground hover:bg-brand-500 text-sm disabled:opacity-50"
            >
              {busy === 'rename' ? <Loader2 size={14} className="inline animate-spin mr-1" /> : null}
              Umbenennen
            </button>
          </div>

          {hasTemplate && (
            <div className="pt-4 border-t border-default">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Auf Vorlage zurücksetzen</div>
              {!confirmReset ? (
                <>
                  <p className="text-xs text-muted-foreground mb-2">
                    Setzt das Layout dieser Seite auf den Stand der ursprünglichen Vorlage zurück.
                    Deine Anpassungen gehen verloren.
                  </p>
                  <button
                    onClick={() => setConfirmReset(true)}
                    disabled={busy !== null}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-default text-foreground hover:bg-muted text-sm"
                  >
                    <RotateCcw size={14} /> Auf Vorlage zurücksetzen
                  </button>
                </>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-foreground">
                    Layout wirklich auf den Vorlagen-Stand zurücksetzen?
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmReset(false)}
                      className="px-3 py-2 rounded-lg border border-default text-foreground hover:bg-muted text-sm"
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={doReset}
                      disabled={busy !== null}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-500 text-sm"
                    >
                      {busy === 'reset' ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                      Zurücksetzen
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="pt-4 border-t border-default">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Gefahrenzone</div>
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                disabled={busy !== null}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-500/40 text-red-500 hover:bg-red-500/10 text-sm"
              >
                <Trash2 size={14} /> Seite löschen
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-foreground">
                  Diese Seite wirklich löschen? Die Widget-Anordnung geht verloren.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="px-3 py-2 rounded-lg border border-default text-foreground hover:bg-muted text-sm"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={doDelete}
                    disabled={busy !== null}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500 text-sm"
                  >
                    {busy === 'delete' ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    Endgültig löschen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
